import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * Asking "are you sure?" without asking the browser to ask.
 *
 * `window.confirm` looks like the honest, dependency-free choice, and it is
 * until the browser decides not to show it. Chrome offers "prevent this page
 * from creating additional dialogs" the second time a page asks, and from then
 * on every call returns false for the rest of that page's life; in-app
 * browsers and any automated one suppress it outright. The call site cannot
 * tell that apart from somebody pressing Cancel, so the button silently does
 * nothing, forever, with no request sent and no error to report — which is
 * exactly how "why can't I remove a student?" begins.
 *
 * So the question is asked inside the page. The call site keeps the same shape
 * — await a boolean, act on true — but the dialog is ours, and nothing outside
 * the app can switch it off.
 */
export interface ConfirmRequest {
  /** The question itself, as a sentence a teacher would say. */
  title: string;
  /** What is about to happen, and what survives it. */
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Destructive actions are red, and open with Cancel focused. */
  tone?: 'danger' | 'normal';
}

export function useConfirm(): { ask: (request: ConfirmRequest) => Promise<boolean>; dialog: ReactNode } {
  const [request, setRequest] = useState<ConfirmRequest | null>(null);
  const settleRef = useRef<((answer: boolean) => void) | null>(null);
  const confirmRef = useRef<HTMLButtonElement | null>(null);
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  const cameFrom = useRef<HTMLElement | null>(null);

  const settle = useCallback((answer: boolean) => {
    const resolve = settleRef.current;
    settleRef.current = null;
    setRequest(null);
    // Back to the button that opened it, so a keyboard user is not returned to
    // the top of the page.
    cameFrom.current?.focus();
    cameFrom.current = null;
    resolve?.(answer);
  }, []);

  const ask = useCallback((next: ConfirmRequest) => {
    // A second question asked while one is open answers the first with "no",
    // rather than leaving its caller waiting on a promise nobody will settle.
    settleRef.current?.(false);
    cameFrom.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setRequest(next);
    return new Promise<boolean>((resolve) => {
      settleRef.current = resolve;
    });
  }, []);

  useEffect(() => {
    if (!request) return undefined;

    // Focus lands on Cancel for anything destructive: the person has already
    // pressed the button once, and a stray Return should not finish the job.
    (request.tone === 'danger' ? cancelRef.current : confirmRef.current)?.focus();

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        settle(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [request, settle]);

  const dialog = request ? (
    <div
      className="modal"
      role="presentation"
      // mousedown rather than click, so a drag that ends outside the card —
      // selecting the text in it, usually — does not read as "cancel".
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) settle(false);
      }}
    >
      <div className="modal__card" role="alertdialog" aria-modal="true" aria-labelledby="confirm-title">
        <h2 className="modal__title" id="confirm-title">
          {request.title}
        </h2>
        {request.body ? <div className="modal__body">{request.body}</div> : null}
        <div className="modal__actions">
          <button type="button" className="btn" ref={cancelRef} onClick={() => settle(false)}>
            {request.cancelLabel ?? 'Cancel'}
          </button>
          <button
            type="button"
            className={`btn ${request.tone === 'danger' ? 'btn--danger-solid' : 'btn--primary'}`}
            ref={confirmRef}
            onClick={() => settle(true)}
          >
            {request.confirmLabel ?? 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return { ask, dialog };
}
